import dedent from 'ts-dedent';
import { ColumnDataType, Language, Provider, WorkflowComponentParamType } from '../types';
import { addColumn } from '../utils/schemas';
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent';
class DifferenceDB extends SingleOutputWorkflowComponent {
    constructor() {
        super({
            name: 'native.difference',
            group: 'Joins',
            title: 'ST Difference',
            description: `
      This component calculates the difference between the geographies
      of the input table and the combined boundary of the geographies in the second table.
      `,
            starred: true
        }, [
            {
                name: 'source',
                type: WorkflowComponentParamType.InputTable,
                title: 'Source table'
            },
            {
                name: 'polygons',
                type: WorkflowComponentParamType.InputTable,
                title: 'Polygons table'
            },
            {
                name: 'geosource',
                type: WorkflowComponentParamType.Column,
                title: 'Geo column in source table',
                parent: 'source',
                dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
            },
            {
                name: 'geopolygons',
                type: WorkflowComponentParamType.Column,
                title: 'Geo column in polygons table',
                parent: 'polygons',
                dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
            }
        ]);
        this.supportedProviders = [Provider.DatabricksRest];
    }
    async getCode({ workflow, params, connection, codeType }) {
        let outputTablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'result', codeType);
        const preparedParams = this._prepareParameters(params);
        const leftOutputTableName = connection.quoteName(outputTablename + '_left');
        const rightOutputTableName = connection.quoteName(outputTablename + '_right');
        const select = dedent `WITH source AS (
      SELECT /*+ REPARTITION(40, ${preparedParams.geosource}) */
      *, ${connection.processInputGeogColumn(preparedParams.geosource)} AS ${preparedParams.geosource}_processed FROM ${leftOutputTableName}
    ),
    polygons AS (
      SELECT /*+ REPARTITION(40, ${preparedParams.geopolygons}) */
      *, ${connection.processInputGeogColumn(preparedParams.geopolygons)} AS ${preparedParams.geopolygons}_processed FROM ${rightOutputTableName}
    )
    SELECT /*+ REPARTITION(40) */
      ${connection.processOutputGeogColumn(`ST_DIFFERENCE(polyg.geo, s.${preparedParams.geosource}_processed)`)}
        AS ${params.geosource}_diff, s.* EXCEPT(${preparedParams.geosource}_processed, __carto_bounds_xmin, __carto_bounds_xmax, __carto_bounds_ymin, __carto_bounds_ymax)
    FROM
      source s,
      (
      SELECT
        MIN(__carto_bounds_xmin) AS __carto_bounds_xmin,
        MAX(__carto_bounds_xmax) AS __carto_bounds_xmax,
        MIN(__carto_bounds_ymin) AS __carto_bounds_ymin,
        MAX(__carto_bounds_ymax) AS __carto_bounds_ymax,
        ST_UNION_AGG(${connection.processInputGeogColumn(params.geopolygons)}) geo
        FROM polygons
      ) polyg
    WHERE
        (s.__carto_bounds_xmax >= polyg.__carto_bounds_xmin
        AND s.__carto_bounds_xmin <= polyg.__carto_bounds_xmax
        AND s.__carto_bounds_ymax >= polyg.__carto_bounds_ymin
        AND s.__carto_bounds_ymin <= polyg.__carto_bounds_ymax)
      AND
        ST_INTERSECTS(
          polyg.geo,
          s.${preparedParams.geosource}_processed
        )`;
        const createTable = connection.wrapSQLCodeAsPythonCall(connection.getCreateTempTableQuery(outputTablename, select, codeType, null));
        const generateSpatialFilterTablesQuery = connection.generateSpatialFilterTables(connection.quoteName(preparedParams.source), connection.quoteName(preparedParams.polygons), preparedParams.geosource, preparedParams.geopolygons, leftOutputTableName, rightOutputTableName);
        const query = `${generateSpatialFilterTablesQuery}\n${createTable}`;
        return {
            query,
            outputs: { result: outputTablename }
        };
    }
    language(provider) {
        return Language.Python;
    }
    canProvideOutputSchemas() {
        return true;
    }
    getOutputSchema({ columns, params, connection }) {
        return addColumn(columns.source, {
            name: `${params.geosource}_diff`,
            type: connection.getGeoDataType()
        });
    }
}
export { DifferenceDB };
